import { Button } from "@dropbox/dig-components/buttons";
import { Chip } from "@dropbox/dig-components/chip";
import { TextInput } from "@dropbox/dig-components/text_fields";
import { UIIcon } from "@dropbox/dig-icons";
import {
  AddLine,
  LockLine,
  SearchLine,
  TeamLine,
} from "@dropbox/dig-icons/dist/mjs/assets";
import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import { VoiceSettingsModal } from "@mirage/mosaics/ComposeAssistant/components/settings/VoiceSettingsModal";
import { AccessType } from "@mirage/shared/compose/assist-api";
import type {
  ComposeVoice,
  DefaultComposeVoice,
} from "@mirage/shared/compose/compose-voice";
import {
  createDefaultTones,
  isPreConfiguredVoiceID,
} from "@mirage/shared/compose/compose-voice";
import i18n from "@mirage/translations";
import { memo, useCallback, useMemo, useState } from "react";
import { ToneCard } from "./ToneCard";
import { ToneDetailView } from "./ToneDetailView";
import styles from "./TonesTab.module.css";

const TONE_FILTERS = {
  YOUR: "your",
  COMPANY: "company",
} as const;

type ToneFilterType = (typeof TONE_FILTERS)[keyof typeof TONE_FILTERS];

interface TonesTabProps {
  voices: ComposeVoice[];
  onUseVoice: (voiceID: string, voiceName: string) => void;
  currentVoiceID?: string | null;
  saveVoice: (voice: ComposeVoice) => Promise<void | string>;
  onDeleteVoice: (voiceId: string) => void;
  setCreatedVoiceSnackbarMsg: (msg: string | undefined) => void;
  voiceSettingsSnackbar: React.ReactNode;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: {
      actionSurfaceComponent?: ActionSurfaceComponent;
    },
  ) => void;
}

export const TonesTab = memo(
  ({
    voices,
    onUseVoice,
    currentVoiceID,
    saveVoice,
    onDeleteVoice,
    setCreatedVoiceSnackbarMsg: _setCreatedVoiceSnackbarMsg,
    voiceSettingsSnackbar,
    logComposeEvent,
  }: TonesTabProps) => {
    const defaultTones = useMemo(() => createDefaultTones(), []);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedFilters, setSelectedFilters] = useState<ToneFilterType[]>(
      [],
    );
    const [selectedTone, setSelectedTone] = useState<
      ComposeVoice | DefaultComposeVoice | null
    >(null);
    const [voiceSettingsModalState, setVoiceSettingsModalState] = useState<{
      isOpen: boolean;
      toneID?: string;
    }>({ isOpen: false });

    const ownedTones = useMemo(() => {
      return voices
        .filter((tone) => tone.isOwned === true)
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }, [voices]);

    const teamTones = useMemo(() => {
      return voices
        .filter(
          (tone) =>
            tone.isOwned !== true && tone.accessType === AccessType.TEAM,
        )
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }, [voices]);

    const sortedDefaultTones = useMemo(() => {
      return [...defaultTones].sort((a, b) =>
        (a.name || "").localeCompare(b.name || ""),
      );
    }, [defaultTones]);

    // Order: Owned (user's) -> Team -> Default (all sorted alphabetically within their category)
    const allTones = useMemo(() => {
      return [...ownedTones, ...teamTones, ...sortedDefaultTones];
    }, [ownedTones, teamTones, sortedDefaultTones]);

    const filteredTones = useMemo(() => {
      let filtered = allTones;

      if (searchQuery) {
        filtered = filtered.filter((tone) =>
          tone.name?.toLowerCase().includes(searchQuery.toLowerCase()),
        );
      }

      const hasFilters = selectedFilters.length > 0;
      if (
        hasFilters &&
        !selectedFilters.includes(TONE_FILTERS.YOUR) &&
        !selectedFilters.includes(TONE_FILTERS.COMPANY)
      ) {
        return filtered;
      }

      if (
        hasFilters &&
        (selectedFilters.includes(TONE_FILTERS.YOUR) ||
          selectedFilters.includes(TONE_FILTERS.COMPANY))
      ) {
        filtered = filtered.filter((tone) => {
          // Check if it's a default tone (from defaultTones array)
          const isDefaultTone =
            !("accessType" in tone) || isPreConfiguredVoiceID(tone.id);

          // Default tones should only show when no filters are selected
          if (isDefaultTone) {
            return (
              !selectedFilters.includes(TONE_FILTERS.YOUR) &&
              !selectedFilters.includes(TONE_FILTERS.COMPANY)
            );
          }

          if (
            selectedFilters.includes(TONE_FILTERS.YOUR) &&
            selectedFilters.includes(TONE_FILTERS.COMPANY)
          ) {
            return tone.isOwned === true || tone.accessType === AccessType.TEAM;
          }

          if (selectedFilters.includes(TONE_FILTERS.YOUR)) {
            return tone.isOwned === true;
          }

          if (selectedFilters.includes(TONE_FILTERS.COMPANY)) {
            return tone.isOwned !== true && tone.accessType === AccessType.TEAM;
          }

          return false;
        });
      }

      return filtered;
    }, [allTones, searchQuery, selectedFilters]);

    const handleToneClick = useCallback(
      (tone: ComposeVoice | DefaultComposeVoice) => {
        setSelectedTone(tone);
      },
      [],
    );

    const handleUseTone = useCallback(
      (toneId: string, toneName: string) => {
        onUseVoice(toneId, toneName);
      },
      [onUseVoice],
    );

    const handleEditTone = useCallback(() => {
      if (selectedTone && !isPreConfiguredVoiceID(selectedTone.id)) {
        setVoiceSettingsModalState({
          isOpen: true,
          toneID: selectedTone.id,
        });
        setSelectedTone(null);
      }
    }, [selectedTone]);

    const handleBackToGrid = useCallback(() => {
      setSelectedTone(null);
    }, []);

    const handleFilterToggle = useCallback((filter: ToneFilterType) => {
      setSelectedFilters((prev) =>
        prev.includes(filter)
          ? prev.filter((f) => f !== filter)
          : [...prev, filter],
      );
    }, []);

    const handleCreateTone = useCallback(() => {
      setVoiceSettingsModalState({ isOpen: true });
    }, []);

    const handleCloseVoiceModal = useCallback(() => {
      setVoiceSettingsModalState({ isOpen: false });
    }, []);

    if (selectedTone) {
      const toneForDetailView: ComposeVoice =
        "accessType" in selectedTone
          ? selectedTone
          : {
              id: selectedTone.id,
              dataId: selectedTone.dataId,
              name: selectedTone.name || "",
              description: selectedTone.description || "",
              accessType: AccessType.INDIVIDUAL,
              sources: [],
              rev: 0,
            };

      return (
        <ToneDetailView
          tone={toneForDetailView}
          onBack={handleBackToGrid}
          onUseTone={handleUseTone}
          onEditTone={handleEditTone}
          onDeleteTone={onDeleteVoice}
        />
      );
    }

    return (
      <div className={styles.TonesTab}>
        <div className={styles.TonesTabHeader}>
          <TextInput
            type="text"
            size="large"
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearchQuery(e.target.value)
            }
            placeholder={i18n.t("ai_tool_library_search_tones")}
            className={styles.TonesTabSearch}
            withLeftAccessory={<UIIcon src={SearchLine} size="small" />}
          />
          <div className={styles.TonesTabFilters}>
            <div className={styles.TonesTabFilterChips}>
              <Chip
                variant="standard"
                size="medium"
                isSelected={selectedFilters.includes(TONE_FILTERS.YOUR)}
                onClick={() => handleFilterToggle(TONE_FILTERS.YOUR)}
              >
                <Chip.IconAccessory>
                  <UIIcon src={LockLine} size="small" />
                </Chip.IconAccessory>
                <Chip.Content>
                  {i18n.t("ai_tool_library_your_tones")}
                </Chip.Content>
              </Chip>
              <Chip
                variant="standard"
                size="medium"
                isSelected={selectedFilters.includes(TONE_FILTERS.COMPANY)}
                onClick={() => handleFilterToggle(TONE_FILTERS.COMPANY)}
              >
                <Chip.IconAccessory>
                  <UIIcon src={TeamLine} size="small" />
                </Chip.IconAccessory>
                <Chip.Content>
                  {i18n.t("ai_tool_library_company_tones")}
                </Chip.Content>
              </Chip>
            </div>
            <Button
              variant="primary"
              size="medium"
              onClick={handleCreateTone}
              className={styles.TonesTabCreateButton}
              withIconStart={<UIIcon src={AddLine} size="small" />}
            >
              {i18n.t("ai_tool_library_create_tone")}
            </Button>
          </div>
        </div>
        <div className={styles.TonesTabGrid}>
          {filteredTones.map((tone) => {
            const toneForCard: ComposeVoice =
              "accessType" in tone
                ? tone
                : {
                    id: tone.id,
                    dataId: tone.dataId,
                    name: tone.name || "",
                    description: tone.description || "",
                    accessType: AccessType.INDIVIDUAL,
                    sources: [],
                    rev: 0,
                  };
            return (
              <ToneCard
                key={tone.id}
                tone={toneForCard}
                onClick={() => handleToneClick(tone)}
                isSelected={currentVoiceID === tone.id}
              />
            );
          })}
        </div>
        {voiceSettingsSnackbar}
        {voiceSettingsModalState.isOpen && (
          <VoiceSettingsModal
            voices={voices}
            saveVoice={async (voice: ComposeVoice) => {
              await saveVoice(voice);
            }}
            onDeleteVoice={onDeleteVoice}
            initialVoiceID={voiceSettingsModalState.toneID}
            onRequestClose={handleCloseVoiceModal}
            logComposeEvent={logComposeEvent}
            initialStepIndex={voiceSettingsModalState.toneID ? -1 : 0}
          />
        )}
      </div>
    );
  },
);

TonesTab.displayName = "TonesTab";
